/**
 * Totales de pedido para exportación Excel.
 * Usa campos del API si existen; si no, calcula desde productos (misma lógica que calculateTotals).
 * Con Cliente Neto activo (p. ej. cliente_neto: 1 en raíz del pedido), el IVA exportado es siempre 0.
 */

import { parseClienteNetoFlag } from './clienteNetoFlag'

function hasDefinedMoney(value: unknown): boolean {
  return value !== undefined && value !== null && value !== ''
}

function formatMoney(amount: number): string {
  return (Number.isFinite(amount) ? amount : 0).toFixed(2)
}

function parseMoney(value: unknown): number {
  const n = parseFloat(String(value).replace(',', '.'))
  return Number.isFinite(n) ? n : 0
}

/** Cliente Neto en raíz del pedido o en extraDetails (API export /orders/all). */
export function isOrderClienteNetoForExport(order: Record<string, unknown>): boolean {
  const extra = order.extraDetails as Record<string, unknown> | undefined
  return (
    parseClienteNetoFlag(order.cliente_neto) ||
    parseClienteNetoFlag(extra?.cliente_neto)
  )
}

/** IVA % mostrado en Excel por línea de producto. */
export function getExportProductIvaPercent(
  order: Record<string, unknown>,
  product: Record<string, unknown>
): string {
  if (isOrderClienteNetoForExport(order)) {
    return '0.00'
  }
  const raw = product.tasa_impuesto ?? product.tax_rate ?? product.iva
  if (raw === undefined || raw === null || raw === '') {
    return '0.00'
  }
  const n = Number(String(raw).replace(',', '.'))
  if (!Number.isFinite(n)) return '0.00'
  return n.toFixed(2)
}

/** Impuesto en valor monetario por línea de producto (precio*cantidad - descuento). */
export function getExportProductTaxAmount(
  order: Record<string, unknown>,
  product: Record<string, unknown>
): string {
  if (isOrderClienteNetoForExport(order)) {
    return '0.00'
  }

  const price = getProductPrice(product)
  const qty = Number(product.quantity) || 1
  const lineSubtotal = price * qty
  const lineDiscount = getProductDiscount(product)
  const taxableBase = Math.max(lineSubtotal - lineDiscount, 0)
  const taxAmount = taxableBase * getTaxRateDecimal(product)

  return formatMoney(taxAmount)
}

function getTaxRateDecimal(product: Record<string, unknown>): number {
  const raw = product.tasa_impuesto ?? product.tax_rate ?? product.iva ?? 19
  const n = Number(String(raw).replace(',', '.'))
  if (!Number.isFinite(n) || n <= 0) return 0.19
  if (n > 0 && n < 1) return n
  return n / 100
}

function getProductPrice(product: Record<string, unknown>): number {
  const raw = product.precio ?? product.price ?? '0'
  return parseMoney(raw)
}

function getProductDiscount(product: Record<string, unknown>): number {
  const raw = product.descuento ?? product.discount ?? 0
  const n = Number(raw)
  return Number.isFinite(n) ? n : 0
}

function getOrderProducts(order: Record<string, unknown>): Record<string, unknown>[] {
  const list = order.products ?? order.line_items ?? order.lineItems
  return Array.isArray(list) ? list : []
}

function calculateTotalsFromProducts(order: Record<string, unknown>) {
  const products = getOrderProducts(order)
  const clienteNeto = isOrderClienteNetoForExport(order)

  let subtotal = 0
  let totalDescuento = 0
  let totalImpuesto = 0

  for (const product of products) {
    const price = getProductPrice(product)
    const qty = Number(product.quantity) || 1
    const lineSubtotal = price * qty
    const lineDiscount = getProductDiscount(product)

    subtotal += lineSubtotal
    totalDescuento += lineDiscount

    if (!clienteNeto) {
      totalImpuesto += (lineSubtotal - lineDiscount) * getTaxRateDecimal(product)
    }
  }

  return {
    subtotal: formatMoney(subtotal),
    total_descuento: formatMoney(totalDescuento),
    total_impuesto: formatMoney(totalImpuesto)
  }
}

function getTotalsFromOrderRoot(order: Record<string, unknown>) {
  const extra = order.extraDetails as Record<string, unknown> | undefined

  const subtotal = order.subtotal ?? extra?.subtotal
  const totalDescuento =
    order.total_descuento ??
    extra?.total_descuento ??
    order.totalDescuento ??
    extra?.totalDescuento
  const totalImpuesto =
    order.total_impuesto ??
    extra?.total_impuesto ??
    order.totalImpuesto ??
    extra?.totalImpuesto

  if (
    !hasDefinedMoney(subtotal) ||
    !hasDefinedMoney(totalDescuento) ||
    !hasDefinedMoney(totalImpuesto)
  ) {
    return null
  }

  return {
    subtotal: formatMoney(parseMoney(subtotal)),
    total_descuento: formatMoney(parseMoney(totalDescuento)),
    total_impuesto: formatMoney(parseMoney(totalImpuesto))
  }
}

export interface OrderExportTotals {
  subtotal: string
  total_descuento: string
  total_impuesto: string
}

export function getOrderExportTotals(order: Record<string, unknown>): OrderExportTotals {
  const fromApi = getTotalsFromOrderRoot(order)
  const totals = fromApi ?? calculateTotalsFromProducts(order)

  if (isOrderClienteNetoForExport(order)) {
    return { ...totals, total_impuesto: '0.00' }
  }

  return totals
}
